import _ from 'lodash';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import { TravelTypes, InsuranceMode } from '../constants';

dayjs.extend(minMax);

export const getInsuranceTripInfo = (trip) => {
  const startDates = [];
  const endDates = [];
  let tripCost = 0;
  const destinations = [];

  const addStart = (date) => {
    if (date) startDates.push(dayjs(date));
  };

  const addEnd = (date) => {
    if (date) endDates.push(dayjs(date));
  };

  if (trip.cruiseItinerary) {
    const cruise = trip.cruiseItinerary;
    addStart(cruise.startDateTime);
    addEnd(cruise.endDateTime);

    const cruiseCost =
      typeof cruise.cost === 'number' ? cruise.cost : cruise.cost?.price || cruise.cost?.value || 0;
    tripCost += cruiseCost;

    if (cruise.ports && cruise.ports.length > 0) {
      const firstPort = cruise.ports[0];
      destinations.push({
        departureDate: cruise.startDateTime,
        arrivalDate: cruise.endDateTime,
        countryCode: firstPort.country,
        stateProvinceCode: firstPort.state,
      });
    }
  }

  if (trip.flightItinerary) {
    const flight = trip.flightItinerary;
    if (flight.flightSegments && flight.flightSegments.length > 0) {
      const segments = flight.flightSegments;
      addStart(segments[0].departureDateTime);
      addEnd(segments[segments.length - 1].arrivalDateTime);

      segments.forEach((segment) => {
        destinations.push({
          departureDate: segment.departureDateTime,
          arrivalDate: segment.arrivalDateTime,
          countryCode: segment.destinationGeoname?.countryCode,
          stateProvinceCode: segment.destinationGeoname?.stateOrCountry,
        });
      });
      const flightCost =
        typeof flight.cost === 'number'
          ? flight.cost
          : flight.cost?.price || flight.cost?.value || 0;
      tripCost += flightCost;
    }
  }

  if (trip.hotelItineraries) {
    const hotelItineraries = Object.values(trip.hotelItineraries);

    hotelItineraries.forEach(({ hotel }) => {
      addStart(hotel.checkInStartTime);
      addEnd(hotel.checkOutEndTime);

      tripCost += hotel.price.totalAfterDiscount;

      if (hotel?.geoname) {
        destinations.push({
          departureDate: hotel.checkInStartTime,
          arrivalDate: hotel.checkOutEndTime,
          countryCode: hotel.geoname.countryCode,
          stateProvinceCode: hotel.geoname.stateOrCountry || '',
        });
      }
    });
  }

  if (trip.rentalCarItineraries) {
    const cars = Object.values(trip.rentalCarItineraries);
    cars.forEach((car) => {
      addStart(car.pickUpDateTime);
      addEnd(car.dropOffDateTime);
      const carCost =
        typeof car.cost === 'number' ? car.cost : car.cost?.price || car.cost?.value || 0;
      tripCost += carCost;

      if (car.originGeoname?.countryCode) {
        destinations.push({
          departureDate: car.pickUpDateTime,
          arrivalDate: car.dropOffDateTime,
          countryCode: car.originGeoname.countryCode,
          stateProvinceCode: car.originGeoname.stateOrCountry || '',
        });
      }
    });
  }

  const startDate = startDates.length > 0 ? dayjs.min(startDates) : undefined;

  const endDate = endDates.length > 0 ? dayjs.max(endDates) : undefined;

  return {
    startDate: startDate?.format('YYYY-MM-DD'),
    endDate: endDate?.format('YYYY-MM-DD'),
    tripCost,
    depositDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    destinations,
  };
};

// Determines insuranceTripInfo at booking time based on insurance mode.
// standaloneTripInfo must be pre-selected by the caller (requires yield select in a saga).
export const getInsuranceTripInfoForBook = (trip, standaloneTripInfo) => {
  if (trip.insuranceItinerary?.insuranceMode === InsuranceMode.Standalone) {
    // User entered trip dates manually in the insurance form
    return standaloneTripInfo;
  }
  if (trip.insuranceItinerary?.insuranceMode === InsuranceMode.AddToTrip) {
    // tripInfo was computed and saved when user configured insurance on the AddInsurance page;
    // selectedTrips[tripId] may no longer contain original itineraries after saveTripSuccess
    // replaced the trip with the minimal /trips/save response
    return trip.insuranceItinerary.tripInfo;
  }
  // AddToBook: compute fresh from current trip itineraries
  return getInsuranceTripInfo(trip);
};

export const validateGetQuotesForm = (tripInfo, passengers, { skipDestinations = false } = {}) => {
  const errors = [];
  const passengerList = Object.values(passengers || {});

  if (!tripInfo?.startDate) errors.push('Departure date is required');
  if (!tripInfo?.endDate) errors.push('Return date is required');
  if (
    tripInfo?.startDate &&
    tripInfo?.endDate &&
    dayjs(tripInfo.startDate).isAfter(dayjs(tripInfo.endDate))
  )
    errors.push('Return date must be the same or later than departure date');
  if (!tripInfo?.depositDate) errors.push('Deposit date is required');
  if (
    tripInfo?.tripCost === undefined ||
    tripInfo?.tripCost === null ||
    Number(tripInfo.tripCost) < 0
  )
    errors.push('Total trip cost must be 0 or greater');
  if (!skipDestinations && (!tripInfo?.destinations || tripInfo.destinations.length === 0))
    errors.push('At least one destination is required');

  passengerList.forEach((passenger, index) => {
    const passengerNumber = index + 1;

    if (!passenger?.firstName) errors.push(`Passenger ${passengerNumber}: First name is required`);
    if (!passenger?.lastName) errors.push(`Passenger ${passengerNumber}: Last name is required`);
    if (!passenger?.birthDate)
      errors.push(`Passenger ${passengerNumber}: Date of birth is required`);
    if (!passenger?.countryCode)
      errors.push(`Passenger ${passengerNumber}: Address country is required`);
    if (passenger?.countryCode === 'US' && !passenger?.stateProvCode)
      errors.push(`Passenger ${passengerNumber}: Address state is required for US addresses`);
  });

  return errors;
};

export const getPurchasedInsurances = (tripConfirmation) => {
  const insuranceInfos = _.chain(tripConfirmation?.bookingResponses || [])
    .filter(
      (b) =>
        b.mode === TravelTypes.insurance &&
        b.insuranceInfo.success &&
        b.insuranceInfo.canceledOrderInfo?.success !== true,
    )
    .map((b) => b.insuranceInfo)
    .value();
  return insuranceInfos;
};

export const getCanceledInsurances = (tripConfirmation) => {
  const insuranceInfos = _.chain(tripConfirmation?.bookingResponses || [])
    .filter(
      (b) =>
        b.mode === TravelTypes.insurance &&
        b.insuranceInfo.success &&
        b.insuranceInfo.canceledOrderInfo?.success === true,
    )
    .map((b) => b.insuranceInfo)
    .value();
  return insuranceInfos;
};
